import { useCallback, useEffect, useState } from "react";

export interface CookiePreferences {
  necessary: boolean;
  analytics: boolean;
  marketing: boolean;
}

export interface CookieConsentState {
  isVisible: boolean;
  isExpanded: boolean;
  preferences: CookiePreferences;
  showSettings: boolean;
  isRejected: boolean;
  isAccepted: boolean;
  showAcceptedNotification: boolean;
}

export interface CookieConsentActions {
  setVisible: (visible: boolean) => void;
  setExpanded: (expanded: boolean) => void;
  setPreferences: (preferences: Partial<CookiePreferences>) => void;
  setShowSettings: (show: boolean) => void;
  setRejected: (rejected: boolean) => void;
  setAccepted: (accepted: boolean) => void;
  setShowAcceptedNotification: (show: boolean) => void;
  acceptAll: () => void;
  rejectAll: () => void;
  savePreferences: () => void;
  reset: () => void;
}

import {
  CONSENT_ANSWERED_ATTR,
  COOKIE_CONSENT_KEY,
  COOKIE_CONSENT_VERSION,
  COOKIE_CONSENT_VERSION_KEY,
  COOKIE_PREFERENCES_KEY,
} from "@/shared/lib/cookie-consent-keys";

/** Событие окна: согласие изменилось — трекеры перечитывают его без перезагрузки. */
export const COOKIE_CONSENT_CHANGED_EVENT = "cookie-consent:changed";

/**
 * Разрешена ли аналитика с куки. Вне браузера, без ответа и при ответе на
 * старую версию баннера — нет: трекер без согласия не грузится никогда.
 */
export function hasAnalyticsConsent(): boolean {
  if (typeof window === "undefined") return false;
  try {
    if (localStorage.getItem(COOKIE_CONSENT_VERSION_KEY) !== COOKIE_CONSENT_VERSION) {
      return false;
    }
    const raw = localStorage.getItem(COOKIE_PREFERENCES_KEY);
    if (!raw) return false;
    return (JSON.parse(raw) as Partial<CookiePreferences>).analytics === true;
  } catch {
    return false;
  }
}

function persistConsent(status: "accepted" | "rejected", prefs: CookiePreferences) {
  try {
    localStorage.setItem(COOKIE_CONSENT_KEY, status);
    localStorage.setItem(COOKIE_PREFERENCES_KEY, JSON.stringify(prefs));
    localStorage.setItem(COOKIE_CONSENT_VERSION_KEY, COOKIE_CONSENT_VERSION);
    document.documentElement.setAttribute(CONSENT_ANSWERED_ATTR, "");
  } catch {
    // Приватный режим / заблокированное хранилище: выбор живёт до перезагрузки.
  }
  window.dispatchEvent(new Event(COOKIE_CONSENT_CHANGED_EVENT));
}

const defaultPreferences: CookiePreferences = {
  necessary: true,
  analytics: false,
  marketing: false,
};

export function useCookieConsent(): CookieConsentState & CookieConsentActions {
  // true с первого рендера: баннер уходит в SSR-HTML (KAN-1244). У ответивших
  // его скрывает CSS по атрибуту <html> ещё до гидратации, а эффект ниже
  // убирает из дерева.
  const [isVisible, setIsVisible] = useState(true);
  const [isExpanded, setIsExpanded] = useState(false);
  const [preferences, setPreferences] =
    useState<CookiePreferences>(defaultPreferences);
  const [showSettings, setShowSettings] = useState(false);
  const [isRejected, setIsRejected] = useState(false);
  const [isAccepted, setIsAccepted] = useState(false);
  const [showAcceptedNotification, setShowAcceptedNotification] =
    useState(false);

  // Загружаем сохраненные настройки при инициализации
  useEffect(() => {
    let consent: string | null = null;
    let savedPreferences: string | null = null;
    let version: string | null = null;
    try {
      consent = localStorage.getItem(COOKIE_CONSENT_KEY);
      savedPreferences = localStorage.getItem(COOKIE_PREFERENCES_KEY);
      version = localStorage.getItem(COOKIE_CONSENT_VERSION_KEY);
    } catch {
      // Хранилище недоступно — спрашиваем, как в первый раз.
    }

    if (!consent || version !== COOKIE_CONSENT_VERSION) {
      setIsVisible(true);
      setIsExpanded(true);
    } else {
      setIsVisible(false);
    }

    if (savedPreferences) {
      try {
        const parsed = JSON.parse(savedPreferences);
        setPreferences({ ...defaultPreferences, ...parsed });
      } catch {
        // Игнорируем ошибки парсинга
      }
    }
  }, []);

  const acceptAll = useCallback(() => {
    const allAccepted = {
      necessary: true,
      analytics: true,
      marketing: true,
    };

    setPreferences(allAccepted);
    setIsAccepted(true);
    setIsRejected(false);
    setIsVisible(false);
    setShowAcceptedNotification(true);

    persistConsent("accepted", allAccepted);

    // Скрываем уведомление через 3 секунды
    setTimeout(() => {
      setShowAcceptedNotification(false);
    }, 3000);
  }, []);

  const rejectAll = useCallback(() => {
    const onlyNecessary = {
      necessary: true,
      analytics: false,
      marketing: false,
    };

    setPreferences(onlyNecessary);
    setIsRejected(true);
    setIsAccepted(false);
    setIsVisible(false);

    persistConsent("rejected", onlyNecessary);
  }, []);

  const savePreferences = useCallback(() => {
    setIsAccepted(true);
    setIsRejected(false);
    setIsVisible(false);
    setShowAcceptedNotification(true);

    persistConsent("accepted", preferences);

    // Скрываем уведомление через 3 секунды
    setTimeout(() => {
      setShowAcceptedNotification(false);
    }, 3000);
  }, [preferences]);

  const reset = useCallback(() => {
    setIsVisible(true);
    setIsExpanded(false);
    setPreferences(defaultPreferences);
    setShowSettings(false);
    setIsRejected(false);
    setIsAccepted(false);
    setShowAcceptedNotification(false);

    try {
      localStorage.removeItem(COOKIE_CONSENT_KEY);
      localStorage.removeItem(COOKIE_PREFERENCES_KEY);
      localStorage.removeItem(COOKIE_CONSENT_VERSION_KEY);
    } catch {
      // см. persistConsent
    }
    document.documentElement.removeAttribute(CONSENT_ANSWERED_ATTR);
    window.dispatchEvent(new Event(COOKIE_CONSENT_CHANGED_EVENT));
  }, []);

  const updatePreferences = useCallback(
    (newPreferences: Partial<CookiePreferences>) => {
      setPreferences((prev) => ({ ...prev, ...newPreferences }));
    },
    [],
  );

  return {
    // State
    isVisible,
    isExpanded,
    preferences,
    showSettings,
    isRejected,
    isAccepted,
    showAcceptedNotification,

    // Actions
    setVisible: setIsVisible,
    setExpanded: setIsExpanded,
    setPreferences: updatePreferences,
    setShowSettings,
    setRejected: setIsRejected,
    setAccepted: setIsAccepted,
    setShowAcceptedNotification,
    acceptAll,
    rejectAll,
    savePreferences,
    reset,
  };
}
